import { Alert, Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { FilterBar } from "../components/filters/FilterBar";
import { Panel } from "../components/layout/Panel";
import { useMetadata } from "../hooks/useMetadata";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import { CompGrid, type Column } from "../components/grid/CompGrid";

interface InstanceRow {
  serviceName: string;
  instanceId: string;
  firstSeen: Date;
  lastSeen: Date;
}

export function PageServiceInstances() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range } = useTimeRangeFromQuery();
  const { metadata, loading: metadataLoading, error: metadataError } = useMetadata(serviceSlug, {
    timeRange: range,
  });

  const instanceRows = useMemo<InstanceRow[]>(
    () => {
      if (!metadata) return [];

      return metadata.services.flatMap((svc) =>
        svc.instances.map((instance) => ({
          serviceName: svc.service_name,
          instanceId: instance.instance_id,
          firstSeen: new Date(instance.first_seen),
          lastSeen: new Date(instance.last_seen),
        })),
      );
    },
    [metadata],
  );

  const instanceColumns: Column<InstanceRow>[] = [
    {
      id: "instanceId",
      label: "Instance",
      sortable: true,
      sortValue: (r) => r.instanceId,
      render: (r) => (
        <Typography variant="body2" className="om-text-mono">
          {r.instanceId}
        </Typography>
      ),
    },
    {
      id: "serviceName",
      label: "Service",
      sortable: true,
      sortValue: (r) => r.serviceName,
      render: (r) => <Typography variant="body2">{r.serviceName}</Typography>,
    },
    {
      id: "firstSeen",
      label: "First Seen",
      sortable: true,
      sortValue: (r) => r.firstSeen,
      render: (r) => <Typography variant="body2">{r.firstSeen.toLocaleString()}</Typography>,
    },
    {
      id: "lastSeen",
      label: "Last Seen",
      sortable: true,
      sortValue: (r) => r.lastSeen,
      render: (r) => <Typography variant="body2">{r.lastSeen.toLocaleString()}</Typography>,
    },
  ];

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product's services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <TimeRangeFilterBar />
        </FilterBar>
      </Panel>
      <Panel title="Instances">
        <CompGrid
          items={instanceRows}
          loading={metadataLoading}
          error={metadataError}
          emptyMessage="No instances found."
          columns={instanceColumns}
          keyExtractor={(r) => `${r.serviceName}-${r.instanceId}`}
        />
      </Panel>
    </Box>
  );
}
